/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable camelcase */
import { getExistingConsent, setConfig } from '@uswitch/rvuconsent'
import { Button, Link, Checkbox } from '@uswitch/spark'
import React, { useState, useEffect, useRef, Fragment } from 'react'
import { useMutation } from 'urql'
import uuidv1 from 'uuid/v1'

import AddressMatcher from '../../../landing-form/address-matcher'
import FormErrorMessage from '../../../landing-form/address-matcher/form-error-message'
import CurrentProviderBox from '../../../landing-form/current-provider-box'
import { ConsentEmailInput, isEmailValid } from '../../../pages/hull/sections/email-capture'
import CAPTURE_CONSENT_MUTATION from '../ms3-mutation.gql'
import { usePageContext } from '~/contexts/page-context'
import STORE_CUSTOMER_MUTATION from '~/graphql/mutations/store-customer'
import captureException from '~/utils/capture-exception'
import { setCookie } from '~/utils/cookies'
import { notEmpty } from '~/utils/empty'
import { QLJAddressFormat } from '~/utils/format-address'
import { getProvidersByPageId, getProvidersByScope } from '~/utils/providers'
import { track, trackSegment } from '~/utils/tracking.js'

setConfig({
  apiPath: 'https://www.uswitch.com/consent',
  brand: 'uswitch'
})

const NoResultsForm = ({
  customerAddress = {},

  provider = '',
  showProviderBox,
  isFetchingAddress,
  redirect,
  showNoResultsMessagingVariant
}) => {
  const [formId, setFormId] = useState(null)
  const [postcode, setPostcode] = useState('')
  const [address, setAddress] = useState({})
  const [currentProvider, setCurrentProvider] = useState(provider)
  const [formError, setFormError] = useState({ formError: false, errorMessage: '', providerError: '' })
  const [isValidPostcode, setValidPostcode] = useState(null)
  const [isValidAddress, setValidAddress] = useState(null)
  const [isValidProvider, setValidProvider] = useState(null)
  const landingFormRef = useRef(null)
  const [, storeCustomer] = useMutation(STORE_CUSTOMER_MUTATION)
  const [, captureUser] = useMutation(CAPTURE_CONSENT_MUTATION)
  const [email, setEmail] = useState()
  const [hasExistingConsent, setHasExistingConsent] = useState(false)
  const [consentCheckboxValue, setConsentCheckboxValue] = useState(false)
  const [isConsentApiSuccess, setIsConsentApiSuccess] = useState(false)
  const { pageId, scope } = usePageContext()

  const formData = {
    input: {
      email,
      source: 'ms3-broadband-availability',
      sourceUrl: 'https://www.uswitch.com/broadband/hull/results',
      consents: [
        {
          name: 'uswitch.soft-opt-in.broadband-availability',
          status: !consentCheckboxValue // this is because the text says 'No, I do not want to receive other money-saving deals and expert insights from Uswitch.' so you actually want it to be set to the inverse of their selection i.e if they select tick it they actually want to opt out
        },
        {
          name: 'uswitch.broadband-availability-alerts',
          status: true
        }
      ],
      address: { uprn: address.uprn, postcode: address.postcode, source: 'ms3-broadband-availability' }
    }
  }

  const { errorMessage, providerError } = formError

  useEffect(() => {
    setFormId(uuidv1())
  }, [])

  useEffect(() => {
    if (isEmailValid(email)) {
      async function getConsent() {
        try {
          const existingConsent = await getExistingConsent({
            email,
            consentName: 'uswitch',
            consentType: 'marketing'
          })
          setHasExistingConsent(existingConsent)
        } catch (e) {
          setHasExistingConsent(false)
        }
      }
      getConsent()
    }
  }, [email])

  const filteredProvidersByPageId = getProvidersByPageId(pageId)
  const providersData = getProvidersByScope(filteredProvidersByPageId, scope)

  useEffect(() => {
    setValidPostcode(notEmpty(postcode))
    setValidAddress(notEmpty(address))
    setFormError({ formError: false, errorMessage: '', providerError: '' }) // @TODO: this may need to change
  }, [postcode, address])

  useEffect(() => {
    setValidProvider(notEmpty(currentProvider))
    setFormError({ formError: false, errorMessage: '', providerError: '' }) // @TODO: this may need to change
  }, [currentProvider])

  useEffect(() => {
    setCurrentProvider(provider)
  }, [provider])

  useEffect(() => {
    const formErroredProps = {
      submit_id: formId,
      form_name: 'ms3-no-results-form',
      form_started_id: formId
    }
    if (errorMessage) {
      trackSegment('Form Errored', {
        ...formErroredProps,
        form_error_text: errorMessage
      })
    }
    if (providerError && notEmpty(address)) {
      trackSegment('Form Errored', {
        ...formErroredProps,
        form_error_text: providerError
      })
    }
  }, [errorMessage, formId, address, providerError]) // only fire provider form error when address has already been set.

  const onSubmit = async (e) => {
    e.preventDefault()

    if (!isValidForm()) {
      return
    }

    // I would have rather set it in the graphql layer but the yoga server library is too old to do that.
    // Updating it would solve the issue but we have no time for that right now.
    const response = await captureUser(formData)
    const customerId = response.data.captureConsent
    setCookie('usci', customerId)

    setIsConsentApiSuccess(!!response?.data?.captureConsent)

    track('Broadband - Availability Checker', 'Submit')
    await saveAddress()

    window.nerdalytics.identify(customerId, () => {
      trackSegment('Form Submitted', {
        form_id: 'availability-notification-signup-form',
        form_is_step_skipped: false,
        form_name: 'availability-notification-signup-form',
        form_step_name: 'ms3-hull-availability-form',
        form_step_id: 'ms3-hull-availability-form',
        form_type: 'update_consent',
        form_step_field: [
          {
            id: 'address',
            name: 'address',
            label: '',
            value: address.summaryLine,
            type: 'text'
          },
          {
            id: 'current-provider',
            name: 'current-provider',
            label: '',
            value: currentProvider,
            type: 'text'
          },
          {
            id: 'email',
            name: 'email',
            label: '',
            value: email,
            type: 'text'
          },
          {
            id: 'postcode',
            name: 'postcode',
            label: '',
            value: address.postcode,
            type: 'text'
          },
          {
            id: 'uprn',
            name: 'uprn',
            label: '',
            value: address.uprn,
            type: 'text'
          },
          {
            id: 'udprn',
            name: 'udprn',
            label: '',
            value: address.udprn,
            type: 'text'
          }
        ],
        org_brand: 'uswitch',
        org_product_category: 'broadband',
        org_subvertical: 'broadband',
        org_vertical: 'communications',
        org_vertical_group: 'home_services'
      })
    })

    if (redirect) {
      window.location = redirect
    }
  }

  const isValidForm = () => {
    if (errorMessage) {
      return false
    }
    if (!isValidPostcode) {
      track('Broadband - availability checker postcode validation error', 'Please enter a valid postcode')
      setFormError({
        formError: true,
        errorMessage: 'Please enter your postcode e.g. SE1 2LH',
        providerError: ''
      })
      return false
    }

    if (!isValidAddress) {
      track('Broadband - availability checker address validation error', 'Please select an address')
      setFormError({ formError: true, errorMessage: 'Please select an address', providerError: '' })
      return false
    }

    if (!isValidProvider && showProviderBox) {
      track('Broadband - availability checker provider validation error', 'Please select a provider')
      setFormError({ formError: true, errorMessage: '', providerError: 'Please select your current provider' })
      return false
    }
    setFormError({ formError: false, errorMessage: '', providerError: '' })
    return true
  }

  const getMutationVariables = (address, formId) => ({
    customer: {
      address: QLJAddressFormat(address),
      formStartedId: formId,
      provider: currentProvider
    }
  })

  const saveAddress = async () => {
    await storeCustomer(getMutationVariables(address, formId)).then((result) => {
      if (result.error) {
        console.error(result.error)
        captureException(result.error)
      }
    })
  }

  const onChangeCurrentProvider = ({ target: { value, providerName } }) => {
    track('Broadband - Provider Capture', providerName)
    setCurrentProvider(value)
  }

  const clearSelectedProvider = () => {
    setCurrentProvider(null)
    track('Broadband - Provider Capture', 'Clear provider')
  }

  const onClick = () =>
    trackSegment('Element Clicked', {
      element_name: 'Privacy notice',
      element_text: 'Privacy notice',
      element_type: 'link'
    })

  return (
    <Fragment>
      {isConsentApiSuccess ? (
        <div sx={{ bg: 'white', borderRadius: '8px' }}>
          <h3 sx={{ mb: '32px' }}>Register for broadband updates with a Uswitch account</h3>
          <div>
            <p>Thanks for signing up!</p>
            <p>You’ll be the first to know as soon as we have new broadband deals, so keep an eye on your inbox.</p>
            <p>If you change your mind and no longer want to be updated, you can unsubscribe any time.</p>
          </div>
          <a
            href='/'
            sx={{
              display: 'block',
              padding: '16px',
              bg: '#F3F3F4',
              maxWidth: ['100%', '100%', '155px'],
              textDecoration: 'none',
              '&:hover': {
                cursor: 'pointer'
              }
            }}
          >
            Go to homepage
          </a>
        </div>
      ) : (
        <Fragment>
          {!showNoResultsMessagingVariant && (
            <h3 sx={{ mb: '24px' }}>Register for broadband updates with a Uswitch account</h3>
          )}
          <form
            onSubmit={onSubmit}
            sx={{ p: 0, background: 'white' }}
            id='hull-landing-form'
            data-nerd
            data-nerd-loaded
            data-tracking-id='hull-landing-form'
            ref={landingFormRef}
          >
            <label sx={{ display: 'block', fontWeight: 700, fontSize: '18px', lineHeight: '24px', mb: '12px' }}>
              Your address
            </label>
            <AddressMatcher
              customerAddress={customerAddress}
              setters={{ setPostcode, setAddress, setFormError }}
              isFetchingAddress={isFetchingAddress}
            />
            {errorMessage && <FormErrorMessage errorMessage={errorMessage} />}
            <div sx={{ visibility: isFetchingAddress ? 'hidden' : 'visible' }}>
              <label sx={{ display: 'block', fontWeight: 700, fontSize: '18px', lineHeight: '24px', mb: '12px' }}>
                Your current provider
              </label>
              <CurrentProviderBox
                currentProvider={currentProvider}
                onChangeCurrentProvider={onChangeCurrentProvider}
                providersData={providersData}
                clearSelectedProvider={clearSelectedProvider}
              />
              {providerError && <FormErrorMessage errorMessage={providerError} />}
            </div>
            <label sx={{ display: 'block', fontWeight: 700, fontSize: '18px', lineHeight: '24px', mb: '12px' }}>
              Your email address
            </label>
            <ConsentEmailInput
              emailAddress={email}
              isEmailValid={isEmailValid(email)}
              onChange={(mail) => {
                setEmail(mail)
              }}
            />

            <div
              sx={{
                display: hasExistingConsent && isEmailValid(email) ? 'flex' : 'none',
                width: '100%',
                mt: 16,
                label: {
                  width: '100%',
                  maxWidth: 'none'
                }
              }}
            >
              <Checkbox
                id={'consent-checkbox'}
                sx={{
                  border: 'none'
                }}
                checked={consentCheckboxValue}
                onChange={(e) => setConsentCheckboxValue(e.target.checked)}
              >
                No, I do not want to receive other money-saving deals and expert insights from Uswitch.
              </Checkbox>
            </div>

            <Button
              variant='primary'
              type='submit'
              disabled={!isEmailValid(email)}
              fullWidth
              sx={{ height: '56px', mt: 16 }}
            >
              Keep me updated
            </Button>
            <Link
              newTab
              text='Privacy notice'
              to='/about-us/privacy-policy/'
              sx={{ mt: 'sm', fontSize: '12px' }}
              onClick={onClick}
            />
          </form>
        </Fragment>
      )}
    </Fragment>
  )
}

export default NoResultsForm
